import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';


const ProjectFormModal = ({ isOpen, onClose, onSubmit, initialData = null }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    client: initialData?.client || '',
    budgetedHours: initialData?.budgetedHours || '',
    hourlyRate: initialData?.hourlyRate || '',
    deadline: initialData?.deadline || '',
    status: initialData?.status || 'active',
    description: initialData?.description || ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'on-hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = 'Project name is required';
    }
    
    if (!formData?.client?.trim()) {
      newErrors.client = 'Client name is required';
    }
    
    if (!formData?.budgetedHours || formData?.budgetedHours <= 0) {
      newErrors.budgetedHours = 'Budgeted hours must be greater than 0';
    }
    
    if (!formData?.hourlyRate || formData?.hourlyRate <= 0) {
      newErrors.hourlyRate = 'Hourly rate must be greater than 0';
    }
    
    if (!formData?.deadline) {
      newErrors.deadline = 'Deadline is required';
    }
    
    setErrors(newErrors);
    return Object?.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const projectData = {
        ...formData,
        id: initialData?.id || `proj-${Date.now()}`,
        budgetedHours: parseFloat(formData?.budgetedHours),
        hourlyRate: parseFloat(formData?.hourlyRate),
        hoursSpent: initialData?.hoursSpent || 0,
        createdAt: initialData?.createdAt || new Date()?.toISOString()
      };
      
      await onSubmit(projectData);
      
      // Reset form
      setFormData({
        name: '',
        client: '',
        budgetedHours: '',
        hourlyRate: '',
        deadline: '',
        status: 'active',
        description: ''
      });
      
      onClose();
    } catch (error) {
      console.error('Error saving project:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-foreground">
              {initialData ? 'Edit Project' : 'Create New Project'}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={handleClose}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input
                label="Project Name"
                placeholder="Enter project name"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />
            </div>
            
            <div>
              <Input
                label="Client"
                placeholder="Enter client name"
                value={formData?.client}
                onChange={(e) => handleInputChange('client', e?.target?.value)}
                error={errors?.client}
                required
              />
            </div>
          </div>

          {/* Project Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Input
                label="Budgeted Hours"
                type="number"
                placeholder="0"
                value={formData?.budgetedHours}
                onChange={(e) => handleInputChange('budgetedHours', e?.target?.value)}
                error={errors?.budgetedHours}
                min="0"
                step="0.5"
                required
              />
            </div>
            
            <div>
              <Input
                label="Hourly Rate ($)"
                type="number"
                placeholder="0.00"
                value={formData?.hourlyRate}
                onChange={(e) => handleInputChange('hourlyRate', e?.target?.value)}
                error={errors?.hourlyRate}
                min="0"
                step="0.01"
                required
              />
            </div>
            
            <div>
              <Select
                label="Status"
                options={statusOptions}
                value={formData?.status}
                onChange={(value) => handleInputChange('status', value)}
                required
              />
            </div>
          </div>

          {/* Deadline */}
          <div>
            <Input
              label="Deadline"
              type="date"
              value={formData?.deadline}
              onChange={(e) => handleInputChange('deadline', e?.target?.value)}
              error={errors?.deadline}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              placeholder="Project description (optional)"
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md text-sm text-foreground placeholder-muted-foreground bg-input focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
            />
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName={isSubmitting ? "Loader2" : "Check"}
              iconPosition="left"
              disabled={isSubmitting}
              className={isSubmitting ? "animate-spin" : ""}
            >
              {isSubmitting ? 'Saving...' : (initialData ? 'Update Project' : 'Create Project')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectFormModal;